import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { device, Grey_, Grey_Link, IconSvg, Input_, Partial_Loader, Slider_ } from 'monica-alexandria'

const SharePanel = styled.div`
  .SliderWrapper {
    max-width: 48rem;

    @media ${device.md} {
      max-width: 100%;
    }
  }
`;

const ShareHeading = styled.div`
  margin-bottom: 1.4rem;

  p {
    margin: 0.4rem 0 0;
    opacity: 0.7;
  }

  h6 {
    padding-top: 0.4rem;
    color: var(--warning);
  }
`;

const ShareInputState = styled.div`
  .FormGroup input {
    border: 1px solid ${(p) => {
      if (p.statusType === "warning") return "var(--warning)";
      if (p.statusType === "error") return "var(--error)";
      return p.theme.low;
    }};
    cursor: ${(p) => (p.statusType === "warning" ? "not-allowed" : "default")};
  }

  opacity: ${(p) => (p.statusType === "warning" ? 0.5 : 1)};
`;

const ShareList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.6rem;
`;

const ShareRowOwner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 1.2rem;
`;

const ShareRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: start;
  gap: 1.2rem;
  padding: var(--normalPads);
  border-radius: var(--smallRadius);
  background: ${(p) => p.theme.mid};

  h5 {
    margin: 0;
    color: ${(p) => p.theme.color};
    font-size: 1.8rem;
    line-height: 1.2;
    word-break: break-word;
  }

  p {
    margin: 0.35rem 0 0;
    opacity: 0.75;
    font-size: 1.35rem;
    line-height: 1.35;
    word-break: break-word;
  }

  @media ${device.sm} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ShareRowPrimary = styled.div`
  min-width: 0;
`;

const ShareRowMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;

  p {
    margin: 0;
    text-align: right;
    font-size: 1.45rem;
    line-height: 1.3;
    color: ${(p) => p.theme.color};
    opacity: 0.85;
  }

  @media ${device.sm} {
    justify-content: flex-start;

    p {
      text-align: left;
    }
  }
`;

const ShareFooter = styled.div`
  padding: 2rem 0;

  @media ${device.md} {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ShareAccessNote = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;

  svg {
    width: 1.5rem;
    fill: ${(p) => p.theme.color};
  }
`;

const ShareLinkActions = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  color: var(--greyDark);
`;

const getRenderableEmail = (emailValue) => {
  if (typeof emailValue === "string") return emailValue;
  if (emailValue && typeof emailValue === "object") {
    return emailValue.email || "";
  }
  return "";
};

const getCollaboratorDisplayName = (userValue = {}) => {
  const rawEmail = getRenderableEmail(userValue?.email);
  const rawName = [
    userValue?.firstname,
    userValue?.lastname,
  ].filter(Boolean).join(" ").trim();

  if (userValue?.name) return userValue.name;
  if (rawName) return rawName;
  if (rawEmail) {
    return rawEmail
      .split("@")[0]
      .split(/[._\s-]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }
  return String(userValue?.username || "").trim();
};

export default function Crm_Share_Panel({
  sharePanelOpen,
  effectiveSharePanelMode,
  shareLoading,
  handleShareBoard,
  closeSharePanel,
  displayBoardName,
  shareLimitReached,
  remainingCollaboratorSlots,
  shareStatus,
  shareEmail,
  onShareEmailChange,
  onShareEmailKeyDown,
  pendingShareEmails,
  addPendingShareEmail,
  removePendingShareEmail,
  shareEmailHasFormatError,
  ownerPanelName,
  ownerPanelEmail,
  ownerResolvedName,
  currentUserEmail,
  collaboratorListUsers,
  handleUnshareBoard,
  linkSharingEnabled,
  handleToggleLinkSharing,
  handleCopyBoardLink,
}) {
  const { t } = useTranslation();

  if (!sharePanelOpen) return null;

  return (
    <SharePanel>
      <Slider_
        title={effectiveSharePanelMode === "owner" ? t("Share board") : t("Collaborators")}
        text={effectiveSharePanelMode === "owner" ? (shareLoading ? t("Sharing...") : t("Share")) : t("Done")}
        onClick={effectiveSharePanelMode === "owner" ? handleShareBoard : closeSharePanel}
        onClose={closeSharePanel}
        grey={t("Close")}
        iconGreyLeft="Xicon"
        onGrey={closeSharePanel}
      >
        <ShareHeading>
          <h3>{displayBoardName}</h3>
          <p>
            {effectiveSharePanelMode === "owner"
              ? t("Add people by email to collaborate on this board")
              : t("People who can access this board")}
          </p>
          {effectiveSharePanelMode === "owner" ? (
            <h6>
              {shareLimitReached
                ? t("You can invite up to 3 people to this board")
                : t("{{count}} invite spots left", { count: remainingCollaboratorSlots })}
            </h6>
          ) : null}
        </ShareHeading>

        {effectiveSharePanelMode === "owner" ? (
          <ShareInputState statusType={shareLimitReached ? "warning" : shareStatus?.type}>
            <Input_
              placeholder={t("Add people by email")}
              value={shareEmail}
              disabled={shareLimitReached && !pendingShareEmails.length}
              onChange={onShareEmailChange}
              onKeyDown={onShareEmailKeyDown}
              list={pendingShareEmails}
              add={t("Add")}
              onAdd={addPendingShareEmail}
              onDelete={(index) => removePendingShareEmail(pendingShareEmails[index])}
              icon_ListItem="User"
              errors={[
                ...(!shareEmail || !shareEmailHasFormatError ? [] : [t("Enter a valid email address")]),
                ...(shareStatus?.type === "error" && shareStatus?.text ? [shareStatus.text] : []),
              ]}
              helpers={[
                ...(shareLimitReached ? [t("You can invite up to 3 people to this board")] : []),
                ...(shareStatus?.type === "warning" && shareStatus?.text ? [shareStatus.text] : []),
              ]}
              successes={shareStatus?.type === "success" && shareStatus?.text ? [shareStatus.text] : []}
            />
          </ShareInputState>
        ) : null}

        {shareLoading ? <Partial_Loader /> : null}

        <ShareList>
          <ShareRow>
            <ShareRowPrimary>
              <h5>{ownerPanelName}</h5>
              <p>{ownerPanelEmail || ownerResolvedName}</p>
            </ShareRowPrimary>
            <ShareRowMeta>
              <p>{t("Owner")}</p>
            </ShareRowMeta>
          </ShareRow>

          {effectiveSharePanelMode !== "owner" ? (
            <ShareRow>
              <ShareRowPrimary>
                <h5>{t("You")}</h5>
                <p>{currentUserEmail}</p>
              </ShareRowPrimary>
              <ShareRowMeta>
                <p>{t("Can access this board")}</p>
              </ShareRowMeta>
            </ShareRow>
          ) : null}

          {collaboratorListUsers.map((sharedValue) => (
            <ShareRow key={`${sharedValue?.userId || sharedValue?.email}`}>
              <ShareRowOwner>
                <ShareRowPrimary>
                  <h5>{getCollaboratorDisplayName(sharedValue)}</h5>
                  {getRenderableEmail(sharedValue?.email) ? (
                    <p>{getRenderableEmail(sharedValue?.email)}</p>
                  ) : null}
                </ShareRowPrimary>
                <ShareRowMeta>
                  <p>{sharedValue?.pending ? t("Invitation sent") : t("Can access this board")}</p>
                </ShareRowMeta>
              </ShareRowOwner>
              {effectiveSharePanelMode === "owner" ? (
                <ShareRowMeta>
                  <Grey_Link
                    text={t("Remove")}
                    noPadding
                    onClick={() => handleUnshareBoard(sharedValue)}
                  />
                </ShareRowMeta>
              ) : null}
            </ShareRow>
          ))}
        </ShareList>

        {effectiveSharePanelMode === "owner" ? (
          <ShareFooter>
            <ShareAccessNote>
              <IconSvg Icon="User" />
              <div>
                <h6>{linkSharingEnabled ? t("Anyone with the link") : t("Restricted")}</h6>
                <p>
                  {linkSharingEnabled
                    ? t("Anyone with this link can open and use this board")
                    : t("Only people you add by email can open this board")}
                </p>
              </div>
            </ShareAccessNote>

            <ShareLinkActions>
              <Grey_
                text={linkSharingEnabled ? t("Disable link access") : t("Enable link access")}
                onClick={handleToggleLinkSharing}
              />
              <Grey_Link
                text={t("Copy link")}
                iconLeft="Copy"
                onClick={handleCopyBoardLink}
              />
            </ShareLinkActions>
          </ShareFooter>
        ) : null}
      </Slider_>
    </SharePanel>
  );
}
